// Redux store configuration with Firestore persistence

import { configureStore } from "@reduxjs/toolkit"
import { createFirestorePersistenceMiddleware } from "./middleware/firestorePersistence"

// Import all slices
import budgetReducer from "./slices/budgetSlice"
import userPreferencesReducer from "./slices/userPreferencesSlice"
import choresReducer from "./slices/choresSlice"
import expensesReducer from "./slices/expensesSlice"
import categoriesReducer from "./slices/categoriesSlice"

// Simple reducer to prevent the "valid reducer" error (keeping for backwards compatibility)
const defaultReducer = (state = {}, action) => {
  // Handle global app actions
  switch (action.type) {
    case "app/hydrationComplete":
      return {
        ...state,
        hydrationComplete: true,
        lastHydrated: action.payload.timestamp,
        userId: action.payload.userId,
      }
    case "app/hydrationError":
      return {
        ...state,
        hydrationError: action.payload.error,
        hydrationComplete: false,
        lastHydrationAttempt: action.payload.timestamp,
      }
    case "app/logoutComplete":
      return {
        hydrationComplete: false,
        lastLogout: action.payload.timestamp,
        userId: null,
        hydrationError: null,
      }
    default:
      return state
  }
}

// Configure Firestore persistence middleware
const firestoreMiddleware = createFirestorePersistenceMiddleware({
  debounceTime: 1000, // Wait 1 second before persisting to avoid excessive writes
  enableRealTimeSync: true, // Enable real-time sync across devices
  onError: (error) => {
    // You can dispatch an error action here if needed
    // store.dispatch({ type: 'app/firestoreError', payload: error });
  },
  transformState: (state) => {
    // Remove any temporary or sensitive data before persisting
    const { isLoading, error, ...persistableState } = state
    return persistableState
  },
})

export const store = configureStore({
  reducer: {
    default: defaultReducer,
    budget: budgetReducer,
    userPreferences: userPreferencesReducer,
    chores: choresReducer,
    expenses: expensesReducer,
    categories: categoriesReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types for serializable check
        ignoredActions: [
          "budget/hydrateFromFirestore",
          "userPreferences/hydrateFromFirestore",
          "chores/hydrateFromFirestore",
          "expenses/hydrateFromFirestore",
          "categories/hydrateFromFirestore",
          "app/hydrationComplete",
          "app/hydrationError",
          "app/logoutComplete",
        ],
        // Ignore these field paths in all actions and state
        ignoredActionsPaths: [
          "payload.timestamp",
          "payload.createdAt",
          "payload.updatedAt",
        ],
        ignoredPaths: ["items.dates"],
      },
    }).concat(firestoreMiddleware),
  devTools: import.meta.env.MODE !== "production" && {
    name: "RyanDymock.com Store",
    trace: true,
    traceLimit: 25,
    actionSanitizer: (action) => ({
      ...action,
      type: action.type,
    }),
    stateSanitizer: (state) => state,
  },
})

// Selectors for app-level state
export const selectAppHydrationStatus = (state) => ({
  hydrationComplete: state.default?.hydrationComplete || false,
  lastHydrated: state.default?.lastHydrated || null,
  userId: state.default?.userId || null,
  hydrationError: state.default?.hydrationError || null,
})

export const selectIsAppHydrated = (state) =>
  state.default?.hydrationComplete || false
export const selectCurrentUserId = (state) => state.default?.userId || null
export const selectHydrationError = (state) =>
  state.default?.hydrationError || null

export default store
